import { APIResponse, APIError, LoadingState } from "../../types/api";
import { env } from "../../config/environment";
import { getGatewayBaseUrl } from "../../config/gateway";
import { authFetchCredentials } from "../../config/authStorage";
import { invalidateSession, refreshSessionTokens } from "../../lib/auth/permissions";
import {
  cookieSessionStillAlive,
  isNonSessionUnauthorizedDetail,
  unauthorizedDetailFromBody,
} from "../../lib/api/interceptors";
import {
  GATEWAY_LONG_REQUEST_TIMEOUT_MS,
  HEADER_REQUEST_ID,
  HEADER_TRACE_ID,
  REQUEST_TIMEOUT,
} from "../../lib/constants/api";
import { readLocaleSnapshot } from "../../lib/i18n/context";
import { v4 as uuidv4 } from "../../lib/uuid";
import { gatewayHttpFailureFromBody } from "../../lib/utils/response";

/** Page reads fail in 30s — do not look "hung" for 5 minutes on a stuck Gateway call. */
const DEFAULT_REQUEST_TIMEOUT_MS = REQUEST_TIMEOUT;
/** Opt-in for Knowledge approve / long Gateway pipelines (was the old default). */
export { GATEWAY_LONG_REQUEST_TIMEOUT_MS };
/** X-Project-ID: set once in ProjectContext; base.request merges into every request. */
let _projectHeaders: Record<string, string> = {};

export function setProjectHeaders(h: Record<string, string>) {
  _projectHeaders = h && typeof h === "object" ? { ...h } : {};
}

/** Project headers (e.g. X-Project-ID) for requests that bypass base.request (e.g. fetch/SSE). */
export function getProjectHeaders(): Record<string, string> {
  return { ..._projectHeaders };
}

function headerMap(headers?: HeadersInit): Record<string, string> {
  const out: Record<string, string> = {};
  if (!headers) return out;
  const set = (key: string, value: string) => {
    // Fetch header records are case-sensitive keys; duplicate Content-Type/content-type
    // makes Chromium send a JSON-string body that FastAPI rejects with 422.
    out[key.toLowerCase()] = value;
  };
  if (headers instanceof Headers) {
    headers.forEach((value, key) => {
      set(key, value);
    });
    return out;
  }
  if (Array.isArray(headers)) {
    for (const [key, value] of headers) {
      set(key, value);
    }
    return out;
  }
  for (const [key, value] of Object.entries(headers)) {
    set(key, value);
  }
  return out;
}

function withCorrelationHeaders(headers?: HeadersInit): Record<string, string> {
  const merged = headerMap(headers);
  const requestIdKey = HEADER_REQUEST_ID.toLowerCase();
  const traceIdKey = HEADER_TRACE_ID.toLowerCase();
  const existingRequestId = String(merged[requestIdKey] || "").trim();
  const requestId = existingRequestId || uuidv4().replace(/-/g, "");
  const existingTraceId = String(merged[traceIdKey] || "").trim();
  const traceId = existingTraceId || requestId;
  merged[requestIdKey] = requestId;
  merged[traceIdKey] = traceId;
  return merged;
}

export class BaseAPIService {
  protected baseURL: string;
  protected timeout: number;
  protected retryAttempts: number;

  constructor(baseURL: string = env.API_BASE_URL || "") {
    let finalBaseURL = baseURL.trim().replace(/\/+$/, "");
    if (!finalBaseURL) {
      finalBaseURL = getGatewayBaseUrl();
    } else if (typeof window !== "undefined" && finalBaseURL.startsWith("http") && !finalBaseURL.endsWith("/api")) {
      // Defense in depth: absolute env URL missing /api suffix on production hostnames
      try {
        const parsed = new URL(finalBaseURL);
        if (parsed.hostname === "apis.9solutions.vn" || parsed.hostname.endsWith(".9solutions.vn")) {
          finalBaseURL = `${parsed.origin}/api`;
        }
      } catch {
        // keep original baseURL
      }
    }
    this.baseURL = finalBaseURL;
    this.timeout = DEFAULT_REQUEST_TIMEOUT_MS;
    this.retryAttempts = 3; // Default retry attempts
  }

  getBaseURL(): string {
    return this.baseURL;
  }

  private async parseResponseBody(response: Response): Promise<unknown> {
    const contentType = response.headers.get("content-type") ?? "";
    if (!contentType.includes("application/json")) {
      return null;
    }
    try {
      return await response.json();
    } catch {
      return null;
    }
  }

  private httpFailure(status: number, statusText: string, body: unknown): APIError {
    const failure = gatewayHttpFailureFromBody(status, statusText, body);
    return {
      code: failure.code,
      message: failure.message,
      details: failure.details,
    };
  }

  private failureResponse<T>(status: number, statusText: string, body: unknown): APIResponse<T> {
    const failure = this.httpFailure(status, statusText, body);
    return {
      success: false,
      error: failure.message,
      errorDetail: failure,
    };
  }

  protected async request<T>(
    endpoint: string,
    options: RequestInit & { timeoutMs?: number } = {},
    retryCount: number = 0,
    includeProjectHeaders: boolean = true,
    authRetried: boolean = false,
  ): Promise<APIResponse<T>> {
    const url = `${this.baseURL}${endpoint}`;
    const { timeoutMs, ...fetchOptions } = options;
    const timeoutBudget =
      typeof timeoutMs === "number" && Number.isFinite(timeoutMs) && timeoutMs > 0
        ? timeoutMs
        : this.timeout;
    const timeoutController = new AbortController();
    const timeoutId = setTimeout(() => timeoutController.abort(), timeoutBudget);
    const externalSignal = fetchOptions.signal;
    if (externalSignal) {
      if (externalSignal.aborted) {
        timeoutController.abort();
      } else {
        externalSignal.addEventListener("abort", () => timeoutController.abort(), { once: true });
      }
    }

    try {
      const optionHeaders = headerMap(fetchOptions.headers);
      const defaultHeaders: Record<string, string> = {
        accept: "application/json",
        ...headerMap(includeProjectHeaders ? _projectHeaders : undefined),
      };
      const hasBody = fetchOptions.body !== undefined && fetchOptions.body !== null && fetchOptions.body !== "";
      if (hasBody && !(fetchOptions.body instanceof FormData) && !optionHeaders["content-type"]) {
        defaultHeaders["content-type"] = "application/json";
      }
      const requestHeaders = withCorrelationHeaders({
        ...defaultHeaders,
        ...optionHeaders,
        "accept-language": readLocaleSnapshot(),
      });
      const response = await fetch(url, {
        ...fetchOptions,
        credentials: authFetchCredentials(),
        signal: timeoutController.signal,
        headers: requestHeaders,
      });

      clearTimeout(timeoutId);

      if (!response) {
        throw new Error("No response received from server");
      }

      const responseEtag = (response.headers.get("etag") || "").trim();

      if (response.status === 304) {
        return {
          success: true,
          notModified: true,
          etag: responseEtag.length > 0 ? responseEtag : undefined,
        };
      }

      if (response.status === 401) {
        const unauthorizedBody = await this.parseResponseBody(response);
        const unauthorizedDetail = unauthorizedDetailFromBody(unauthorizedBody);
        if (isNonSessionUnauthorizedDetail(unauthorizedDetail)) {
          return this.failureResponse<T>(response.status, response.statusText, unauthorizedBody);
        }
        if (!authRetried) {
          const refreshed = await refreshSessionTokens();
          if (refreshed) {
            return this.request<T>(endpoint, options, retryCount, includeProjectHeaders, true);
          }
        }
        if (await cookieSessionStillAlive()) {
          return this.failureResponse<T>(response.status, response.statusText, unauthorizedBody);
        }
        invalidateSession();
        return { success: false, error: "Unauthorized" };
      }

      const body = await this.parseResponseBody(response);

      if (!response.ok) {
        const failure = this.failureResponse<T>(response.status, response.statusText, body);
        const responseRequestId = (response.headers.get(HEADER_REQUEST_ID) || "").trim();
        const requestId = responseRequestId || requestHeaders[HEADER_REQUEST_ID.toLowerCase()] || "";
        if (failure.errorDetail && requestId) {
          const details = failure.errorDetail.details && typeof failure.errorDetail.details === "object" ? { ...failure.errorDetail.details, request_id: requestId } : { request_id: requestId };
          failure.errorDetail = { ...failure.errorDetail, details };
        }
        return failure;
      }

      const withEtag = <R extends APIResponse<T>>(payload: R): R => {
        if (responseEtag.length === 0) {
          return payload;
        }
        return { ...payload, etag: responseEtag };
      };

      const data = body;
      if (data && typeof data === "object" && "success" in data) {
        const envelope = data as Record<string, unknown> & { success: unknown };
        // Response is already wrapped: { success: true, data: ... }
        if (envelope.success) {
          // Prefer explicit data payload if provided
          if (envelope.data) {
            return withEtag({
              success: true,
              data: envelope.data as T,
            });
          }

          // For legacy AI responses without data payload, extract core fields
          if (envelope.ai_response) {
            return withEtag({
              success: true,
              data: {
                response: envelope.ai_response,
                model: envelope.model,
                tokens: envelope.tokens,
                response_time: envelope.response_time,
                conversation_id: envelope.conversation_id,
              } as T,
            });
          }
          return withEtag({
            success: true,
            data: envelope as T,
          });
        } else {
          return this.failureResponse<T>(response.status, response.statusText, data);
        }
      } else {
        // Response is direct data, wrap it
        return withEtag({
          success: true,
          data: data as T,
        });
      }
    } catch (error) {
      clearTimeout(timeoutId);
      // Handle retry logic
      if (retryCount < this.retryAttempts && this.shouldRetry(error)) {
        await this.delay(1000 * Math.pow(2, retryCount)); // Default retry delay
        return this.request<T>(endpoint, options, retryCount + 1, includeProjectHeaders, authRetried);
      }

      return this.handleError(error);
    }
  }

  public async get<T>(endpoint: string, options: RequestInit = {}): Promise<APIResponse<T>> {
    return this.request<T>(endpoint, { ...options, method: "GET" });
  }

  public async post<T>(
    endpoint: string,
    data?: unknown,
    options: RequestInit & { timeoutMs?: number } = {},
  ): Promise<APIResponse<T>> {
    return this.postRequest<T>(endpoint, data, true, options);
  }

  public async postWithoutProjectContext<T>(
    endpoint: string,
    data?: unknown,
    options: RequestInit & { timeoutMs?: number } = {},
  ): Promise<APIResponse<T>> {
    return this.postRequest<T>(endpoint, data, false, options);
  }

  private async postRequest<T>(
    endpoint: string,
    data: unknown,
    includeProjectHeaders: boolean,
    options: RequestInit & { timeoutMs?: number },
  ): Promise<APIResponse<T>> {
    let body: string | FormData | undefined;
    const headers = new Headers(options.headers);
    if (data instanceof FormData) {
      // FormData - don't set Content-Type, let browser set it with boundary
      body = data;
    } else if (data) {
      // JSON data
      body = JSON.stringify(data);
      if (!headers.has("Content-Type")) {
        headers.set("Content-Type", "application/json");
      }
    }
    return this.request<T>(
      endpoint,
      {
        ...options,
        method: "POST",
        body,
        headers,
      },
      0,
      includeProjectHeaders
    );
  }

  public async put<T>(endpoint: string, data?: unknown): Promise<APIResponse<T>> {
    return this.request<T>(endpoint, {
      method: "PUT",
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  public async delete<T>(endpoint: string, options: RequestInit = {}): Promise<APIResponse<T>> {
    return this.request<T>(endpoint, { ...options, method: "DELETE" });
  }

  public async patch<T>(endpoint: string, data?: unknown): Promise<APIResponse<T>> {
    return this.request<T>(endpoint, {
      method: "PATCH",
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  private shouldRetry(error: unknown): boolean {
    if (error instanceof Error && error.name === "AbortError") return false;
    const msg = error instanceof Error ? error.message : "";
    if (msg.includes("502") || msg.includes("503") || msg.includes("504")) return false;
    if (msg.includes("HTTP 5")) return true;
    return false;
  }

  private async delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  private handleError(error: unknown): APIResponse<never> {
    const timedOut = error instanceof Error && error.name === "AbortError";
    const apiError: APIError = {
      code: timedOut ? "TIMEOUT_ERROR" : error instanceof Error ? error.name : "UNKNOWN_ERROR",
      message: timedOut ? "Request timed out" : error instanceof Error ? error.message : "An unknown error occurred",
      details: error instanceof Error ? { stack: error.stack } : undefined,
    };

    return {
      success: false,
      error: apiError.message,
    };
  }

  protected createLoadingState(): LoadingState {
    return {
      isLoading: false,
      error: null,
      retryCount: 0,
    };
  }

  protected updateLoadingState(state: LoadingState, updates: Partial<LoadingState>): LoadingState {
    return { ...state, ...updates };
  }
}
